import { create } from 'zustand';
import { AuthService } from '../services/auth-service/service';
import {
  InitiateResetPasswordInput,
  SignInResponse,
  UserRole,
} from '../services/auth-service/types';
import { axiosInstance } from '../services/axios-instance';
import { errorMessage, parseAxiosError } from '../utils';
import { StateLoading } from '../types';

interface BaseAuthState extends SignInResponse {
  resetSuccess: string;
  forgotPasswordEmail: string;
  signInError: string;
  initiatePasswordResetError: string;
  confirmPasswordResetError: string;
  isLoading: boolean;
  status: string;
  id: number;
}

interface AuthState extends BaseAuthState {
  signIn: { (email: string, password: string): Promise<void> };
  signOut: { (): void };
  refreshSession: { (): Promise<SignInResponse> };
  initiatePasswordReset: { (input: InitiateResetPasswordInput): Promise<void> };
  confirmPasswordReset: {
    (input: {
      username: string;
      code: string;
      newPassword: string;
    }): Promise<void>;
  };
  adminSignUpState: StateLoading<any>;
  resendCodeState: StateLoading<any>;
  changePasswordState: StateLoading<any>;
}

const authService = new AuthService(axiosInstance);

/**
 * Properly loads the state from localStorage.
 * @param rawState The raw state retreived from localStorage
 */
const loadState = (rawState: string): BaseAuthState => {
  const state = JSON.parse(rawState);
  return state;
};

const savedState = localStorage.getItem('authStore');
const defaultState: any = {
  idToken: {
    jwtToken: '',
    payload: {
      sub: '',
      auth_time: 0,
      exp: 0,
      iat: 0,
      email: '',
      given_name: '',
      family_name: '',
    },
  },
  refreshToken: {
    token: '',
  },
  accessToken: {
    jwtToken: '',
    payload: {
      sub: '',
      scope: UserRole.None,
      auth_time: 0,
      exp: 0,
      iat: 0,
      username: '',
    },
  },
  resetSuccess: '',
  forgotPasswordEmail: '',
  signInError: '',
  initiatePasswordResetError: '',
  isLoading: false,
  confirmPasswordResetError: '',
};
const initialState: BaseAuthState = savedState
  ? loadState(savedState)
  : defaultState;

export interface AdminSignupBody {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
}
interface AuthActions {
  adminSignup: (body: AdminSignupBody) => Promise<void>;
  resendCode: (body: { email: string }) => Promise<void>;
  changePassword: (body: {
    email: string;
    newPassword: string;
    oldPassword: string;
  }) => Promise<void>;
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  ...initialState,
  signIn: async (email: string, password: string) => {
    let auth: SignInResponse;
    try {
      auth = await authService.signIn({ email, password });
    } catch (error) {
      set(() => ({ signInError: parseAxiosError(error) }));
      throw error;
    }
    set(() => ({
      ...auth,
      signInError: '',
    }));
    localStorage.setItem('authStore', JSON.stringify(auth));
  },
  signOut: () => {
    localStorage.removeItem('authStore');
    set(() => defaultState);
  },
  refreshSession: async (): Promise<SignInResponse> => {
    const {
      idToken: {
        payload: { email },
      },
      refreshToken: { token },
    } = get();
    const auth = await authService.refreshToken({ email, refreshToken: token });
    set(() => auth);
    localStorage.setItem('authStore', JSON.stringify(auth));
    return auth;
  },
  initiatePasswordReset: async input => {
    try {
      await authService.initiatePasswordReset(input);
      set(() => ({
        forgotPasswordEmail: input.email,
        initiatePasswordResetError: '',
      }));
    } catch (error) {
      set(() => ({ initiatePasswordResetError: parseAxiosError(error) }));
      throw error;
    }
  },
  confirmPasswordReset: async input => {
    set(e => ({ ...e, isLoading: true }));
    try {
      await authService.confirmPasswordReset({
        email: input.username,
        newPassword: input.newPassword,
        code: input.code,
      });
      set(() => ({
        resetSuccess: 'Your password was updated successfully.',
        forgotPasswordEmail: '',
        confirmPasswordResetError: '',
        isLoading: false,
      }));
    } catch (error) {
      set(() => ({
        confirmPasswordResetError: parseAxiosError(error),
        isLoading: false,
      }));
      throw error;
    }
  },
  adminSignUpState: {},
  adminSignup: async body => {
    set({ adminSignUpState: { isLoading: true } });
    try {
      const { data } = await axiosInstance({
        url: '/auth/admin-register',
        method: 'POST',
        data: body,
      });
      set({ adminSignUpState: { isLoading: false, data } });
    } catch (err) {
      set({ adminSignUpState: { isLoading: false, error: errorMessage(err) } });
      throw err;
    }
  },
  resendCodeState: {},
  resendCode: async body => {
    set({ resendCodeState: { isLoading: true } });
    try {
      const { data } = await axiosInstance({
        method: 'POST',
        url: '/auth/resendConfirmationCode',
        data: body,
      });
      set({ resendCodeState: { isLoading: false, data } });
    } catch (err) {
      set({ resendCodeState: { isLoading: false, error: errorMessage(err) } });
      throw err;
    }
  },

  changePasswordState: {},
  changePassword: async body => {
    set({ changePasswordState: { isLoading: true } });
    try {
      const { data } = await axiosInstance({
        method: 'POST',
        url: '/auth/changePassword',
        data: body,
      });
      set({ changePasswordState: { isLoading: false, data } });
    } catch (err) {
      set({
        changePasswordState: { isLoading: false, error: errorMessage(err) },
      });
      throw err;
    }
  },
}));

// Auto refresh token
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (!error.response) {
      return Promise.reject(error);
    }
    // eslint-disable-next-line no-underscore-dangle
    if (error.response.status === 401 && !originalRequest._retry) {
      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retry = true;
      const { refreshSession, signOut } = useAuthStore.getState();
      try {
        const auth = await refreshSession();
        delete originalRequest.headers.Authorization;
        // TODO: Change to auth.accessToken from auth.idToken when
        // backend API already addressed the token mismatch issue.
        originalRequest.headers.Authorization = `${authService.tokenType} ${auth.idToken.jwtToken}`;
        return axiosInstance(originalRequest);
      } catch (err) {
        signOut();
      }
    }
    return Promise.reject(error);
  },
);
